import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Wrench, Package, PackageSearch, Filter, FileDown, Paperclip, Upload, Loader2,
  X, AlertTriangle, ChevronRight, Undo2, Pencil, Ban, RotateCcw, Image as ImgIcon,
  Hammer, Archive,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import ImagemSharePoint from '../../ImagemSharePoint';
import useEhCompacto from '../../../utils/useEhCompacto';
import { perfilNoModulo } from '../../../utils/perfisDerivados';
import { abrirArquivoComToken } from '../../../utils/arquivoComToken';
import { useOrdenacaoTabela, ThOrd as ThOrdBase } from '../../tabelaOrdenavel';
import { semAcento } from '../../../utils/texto';

// =====================================================================
// COMPRAS › Retirada de Equipamento/Peças — a fila que OPERA a esteira.
//
// O FLUXO (o que o item atravessa, e quem dá cada passo) tem TRÊS caminhos, e o
// Compras decide qual ao sair de "Pendente" (DEM-00016, 2026-09-28):
//   normal   Pendente ─(SAP+valor)→ Enviado para Fiscal ─(+nota, envio)→
//            Enviado para Reparo ─(retorno)→ Retorno do Reparo ─(Finalizar)→ Finalizado
//   interno  reparo por FUNCIONÁRIO do Grupo (sem nota): Pendente ─(envio)→
//            Enviado para Reparo → … → Finalizado
//   estoque  guardado no escritório: Pendente ─(observação)→ Finalizado (direto)
//
// ⚠️⚠️ A CADEIA E OS CAMINHOS NÃO ESTÃO ESCRITOS AQUI — vêm do `GET /fluxo`
// (`etapas`, `complementos`, `caminhosEspeciais`). A tela só sabe DESENHAR: qual
// é o próximo passo, quais campos ele pede e quais botões extras a Pendente tem.
// Copiar a lista para cá faria o botão aparecer para quem a rota depois recusa.
//
// ⚠️⚠️ A TABELA MOSTRA COLUNAS DIFERENTES POR ETAPA (2026-09-28). O placar de
// etapas é o SELETOR; a cada etapa selecionada a tabela troca as colunas
// específicas (SAP/valor na fiscal, viagem no envio, encerramento no finalizado)
// e mantém as fixas (equipe, loja, produto, qtd, ações). Sem etapa escolhida,
// mostra a coluna "Etapa". É o padrão das "Avaliações gravadas" do Operacional.
//
// ⚠️⚠️ TODO PASSO É DO COMPRAS. O que o FISCAL faz não é passo, é COMPLEMENTO: a
// nota fiscal, informada enquanto o item está em `Enviado para Fiscal` (só no caminho
// normal), sem mexer no andamento. A tela mostra o botão dele na mesma linha.
//
// ⚠️ A API É A DO MÓDULO LOGÍSTICA (`/api/logistica/retirada`): a tela mora no
// Compras (quem trabalha a fila) e o dado é de lá. Tabela com dois routers
// escrevendo nela é como as regras se separam.
// =====================================================================

const API = '/api/logistica/retirada';
const COR = '#d97706';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const CANCELADA = 'Cancelada';
const ITENS_POR_PAGINA = 25;

// Só a APARÊNCIA de cada status. A ordem e o significado vêm da API.
const ESTILO = {
  'Pendente': { bg: '#f1f5f9', color: '#475569' },
  'Enviado para Fiscal': { bg: '#fef3c7', color: '#92400e' },
  'Enviado para Reparo': { bg: '#dbeafe', color: '#1d4ed8' },
  'Retorno do Reparo': { bg: '#d1fae5', color: '#065f46' },
  'Finalizado': { bg: '#e0e7ff', color: '#3730a3' },
  'Cancelada': { bg: '#e2e8f0', color: '#64748b' },
};
const estiloDe = (s) => ESTILO[s] || ESTILO.Pendente;

// Como desenhar cada campo que a API pede. ⚠️ Isto é APRESENTAÇÃO (rótulo e tipo
// de input); a obrigatoriedade é do servidor. Campo novo na etapa sem entrada
// aqui aparece como texto simples — degrada, não quebra.
const CAMPO = {
  numero_solicitacao_sap: { label: 'Número da solicitação no SAP', tipo: 'texto', dica: 'Ex.: SOL-4471' },
  valor_unitario: { label: 'Valor da peça (unitário)', tipo: 'decimal', dica: 'R$ — o total é calculado pela quantidade' },
  numero_nota_fiscal: { label: 'Número da nota fiscal', tipo: 'texto' },
  nota_fiscal_url: { label: 'Arquivo da nota fiscal', tipo: 'arquivo', accept: '.pdf,.xml', doc: 'nf' },
  // ⚠️ Aceita .txt E .xml: o "txt" da NF eletrônica costuma ser o XML.
  txt_url: { label: 'Arquivo TXT / XML', tipo: 'arquivo', accept: '.txt,.xml', doc: 'txt' },
  // ⚠️ RÓTULO fala de FORNECEDOR desde 10/09; a CHAVE segue `data_envio_manutencao`.
  data_envio_manutencao: { label: 'Data do envio', tipo: 'data' },
  codigo_rastreio: { label: 'Código de rastreio', tipo: 'texto' },
  data_previsao_retorno: { label: 'Previsão de retorno', tipo: 'data', dica: 'a data prometida — é ela que define o atraso' },
  data_retorno_compras: { label: 'Data do retorno', tipo: 'data' },
  // ⚠️ A observação do caminho ESTOQUE: por que o equipamento ficou guardado.
  observacao: { label: 'Observação', tipo: 'textarea', dica: 'por que ficou no estoque do escritório' },
};

const dataBR = (v) => (v ? String(v).slice(0, 10).split('-').reverse().join('/') : '—');
const hojeISO = () => new Date().toISOString().slice(0, 10);
// A peça passou da data prometida e ainda não voltou (só vale com previsão).
const atrasadaNaPrevisao = (p) => !!p.DATA_PREVISAO_RETORNO && !p.DATA_RETORNO_COMPRAS
  && String(p.DATA_PREVISAO_RETORNO).slice(0, 10) < hojeISO();
const num = { fontVariantNumeric: 'tabular-nums' };
const moeda = (v) => Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
// Quantidade decimal: a central vende por metro. Inteira sai sem casas.
const qtd = (v) => (Number(v) % 1 === 0 ? String(Number(v)) : Number(v).toFixed(2).replace('.', ','));
const diasFora = (p) => ((p.DATA_ENVIO_MANUTENCAO && p.DATA_RETORNO_COMPRAS)
  ? Math.round((new Date(p.DATA_RETORNO_COMPRAS) - new Date(p.DATA_ENVIO_MANUTENCAO)) / 86400000) : null);

// De quem é a retirada (equipe) — mapa com fallback para não misturar as duas.
const EQUIPES_SELO = {
  TI: { rotulo: 'T.I', cor: '#6b21a8', fundo: '#f3e8ff' },
  Manutencao: { rotulo: 'Manutenção', cor: '#b45309', fundo: '#fef3c7' },
};
const SeloEquipe = ({ equipe }) => {
  const e = EQUIPES_SELO[equipe] || { rotulo: equipe || '—', cor: '#64748b', fundo: '#f1f5f9' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 999,
      fontSize: 11, fontWeight: 800, color: e.cor, background: e.fundo, whiteSpace: 'nowrap',
    }}>{e.rotulo}</span>
  );
};

// O CAMINHO do item, quando é especial (interno/estoque). ⚠️ O normal NÃO tem
// selo — é o caminho de sempre, e um selo em toda linha viraria ruído.
const CAMINHO_SELO = {
  interno: { rotulo: 'reparo interno', cor: '#7c3aed', fundo: '#f3e8ff', Icone: Hammer },
  estoque: { rotulo: 'estoque', cor: '#0369a1', fundo: '#e0f2fe', Icone: Archive },
};
const SeloCaminho = ({ caminho }) => {
  const c = CAMINHO_SELO[caminho];
  if (!c) return null;
  const { Icone } = c;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 7px', borderRadius: 999,
      fontSize: 10.5, fontWeight: 800, color: c.cor, background: c.fundo, whiteSpace: 'nowrap',
    }}><Icone size={11} /> {c.rotulo}</span>
  );
};
// Como o item foi ENCERRADO (coluna do Finalizado): o texto lê o caminho.
const comoEncerrou = (p) => {
  if (p.CAMINHO === 'interno') return 'reparo interno';
  if (p.CAMINHO === 'estoque') return 'ficou no estoque';
  return 'voltou do reparo';
};

// A paleta dos botões de AÇÃO (padrão da tela de Inscritos do Processo Seletivo):
// quadradinho branco, ícone e borda coloridos, texto só no hover (title). A cor
// cor-codifica a ação e ecoa os selos (roxo=interno, azul=estoque).
const ACAO_TOM = {
  primary: { cor: '#b45309', borda: '#fde68a', hover: '#fffbeb' }, // avançar / o passo principal
  slate: { cor: '#64748b', borda: '#e2e8f0', hover: '#f8fafc' },   // corrigir / desfazer
  vermelho: { cor: '#e11d48', borda: '#fbb6c4', hover: '#fff1f2' }, // cancelar
  verde: { cor: '#059669', borda: '#a7f3d0', hover: '#ecfdf5' },    // reativar
  interno: { cor: '#7c3aed', borda: '#ddd6fe', hover: '#f5f3ff' },  // reparo interno
  estoque: { cor: '#0369a1', borda: '#bae6fd', hover: '#f0f9ff' },  // deixar no estoque
};

const th = { padding: '11px 14px', borderBottom: '1px solid #e2e8f0', fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', whiteSpace: 'nowrap', textAlign: 'left' };
const td = { padding: '11px 14px', fontSize: 13.5, color: '#0f172a', verticalAlign: 'top' };

// ---- ordenação por clique no cabeçalho (ver components/tabelaOrdenavel.js) ----
// Mapa chave-da-COL → campo de dados. Colunas que NÃO estão aqui não ordenam:
// as compostas ("Saiu / Voltou", "Encerramento") e a de Ações.
const CAMPO_COL = {
  equipe: 'EQUIPE', loja: 'LOJA', produto: 'DESCRICAO', qtd: 'QUANTIDADE',
  etapa: 'STATUS', abertaEm: 'CRIADO_EM', solicitante: 'SOLICITANTE',
  sap: 'NUMERO_SOLICITACAO_SAP', valor: 'VALOR_TOTAL', notaFiscal: 'NUMERO_NOTA_FISCAL',
  fornecedorReparo: 'FORNECEDOR_REPARO', saiu: 'DATA_ENVIO_MANUTENCAO',
  rastreio: 'CODIGO_RASTREIO', previsao: 'DATA_PREVISAO_RETORNO', diasFora: 'DIAS_FORA',
};
// ⚠️ Datas em ISO comparam como texto (cronológico); números por valor; o resto
//    cai no texto sem acento. VALOR_TOTAL (valor × qtd, o que a coluna mostra) e
//    DIAS_FORA são derivados.
const soDiaOrd = (v) => (v ? String(v).slice(0, 10) : null);
const valorDeOrdenacao = (item, campo) => {
  switch (campo) {
    case 'QUANTIDADE': return Number(item.QUANTIDADE) || 0;
    case 'VALOR_TOTAL': return item.VALOR_UNITARIO != null ? Number(item.VALOR_UNITARIO) * Number(item.QUANTIDADE) : null;
    case 'DIAS_FORA': return diasFora(item);
    case 'CRIADO_EM': return soDiaOrd(item.CRIADO_EM);
    case 'DATA_ENVIO_MANUTENCAO': return soDiaOrd(item.DATA_ENVIO_MANUTENCAO);
    case 'DATA_PREVISAO_RETORNO': return soDiaOrd(item.DATA_PREVISAO_RETORNO);
    default: return semAcento(item[campo]);
  }
};
const sel = { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, color: '#475569', background: '#fff' };
const campoStyle = { width: '100%', padding: '9px 11px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, color: '#0f172a', boxSizing: 'border-box', fontFamily: 'inherit' };
const rotuloStyle = { display: 'block', fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 6 };
const btnPag = (desab) => ({
  padding: '6px 14px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff',
  color: desab ? '#94a3b8' : '#0f172a', cursor: desab ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600,
});

// ---------------------------------------------------------------------
// Upload de UM documento (NF ou TXT). Sobe na hora e devolve a URL.
// ⚠️ O arquivo sobe ANTES de o passo ser dado; se a pessoa desistir, ele fica
// órfão e o `limpar-fotos-orfas.js` o recolhe.
// ---------------------------------------------------------------------
function DocUpload({ url, onUrl, doc, accept, loja, chave }) {
  const [estado, setEstado] = useState('idle');
  const [nome, setNome] = useState('');
  const [erro, setErro] = useState('');

  const enviar = async (file) => {
    if (!file) return;
    setEstado('enviando'); setErro('');
    try {
      const fd = new FormData();
      fd.append('arquivo', file);
      fd.append('tipo', doc);
      if (loja) fd.append('loja', loja);
      if (chave) fd.append('chave', chave);
      const r = await fetch(`${API}/upload-documento`, { method: 'POST', headers: auth(), body: fd });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.message || 'Falha no envio.');
      onUrl(d.url);
      setNome(d.nome || file.name);
      setEstado('idle');
    } catch (e) {
      setEstado('erro'); setErro(e.message);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <label style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
          padding: '8px 12px', borderRadius: 8, fontSize: 13, fontWeight: 700,
          border: '1px solid', borderColor: url ? '#fde68a' : '#cbd5e1',
          background: url ? '#fffbeb' : '#fff', color: url ? '#92400e' : '#475569',
        }}>
          {estado === 'enviando' ? <Loader2 size={14} /> : url ? <Paperclip size={14} /> : <Upload size={14} />}
          {url ? (nome || 'arquivo anexado') : 'Escolher arquivo'}
          <input type="file" accept={accept} style={{ display: 'none' }}
            onChange={(e) => enviar(e.target.files?.[0])} />
        </label>
        {url && estado !== 'enviando' && (
          <button type="button" onClick={() => { onUrl(''); setNome(''); }} title="Remover"
            style={{ display: 'inline-flex', padding: 5, cursor: 'pointer', borderRadius: 6, border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c' }}>
            <X size={13} />
          </button>
        )}
      </div>
      {erro && <p style={{ margin: '5px 0 0 0', fontSize: 12, color: '#b91c1c' }}>{erro}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------
// Pop-up de um passo do fluxo. Os campos vêm da etapa (que vem da API).
// ⚠️ `opcionais` marca os campos que NÃO são obrigatórios (ex.: o rastreio no
// reparo interno) — só a dica muda; a regra de verdade é do servidor.
// ---------------------------------------------------------------------
function PassoModal({ peca, etapa, titulo, opcionais = [], onFechar, onConfirmar }) {
  const compacto = useEhCompacto();
  const [valores, setValores] = useState(() => {
    // Corrigir abre com o que já está gravado — troca um campo, não redigita todos.
    const de = {};
    (etapa.campos || []).forEach((c) => {
      const mapa = {
        numero_solicitacao_sap: peca.NUMERO_SOLICITACAO_SAP,
        valor_unitario: peca.VALOR_UNITARIO,
        numero_nota_fiscal: peca.NUMERO_NOTA_FISCAL,
        nota_fiscal_url: peca.NOTA_FISCAL_URL,
        txt_url: peca.TXT_URL,
        data_envio_manutencao: peca.DATA_ENVIO_MANUTENCAO && String(peca.DATA_ENVIO_MANUTENCAO).slice(0, 10),
        codigo_rastreio: peca.CODIGO_RASTREIO,
        data_previsao_retorno: peca.DATA_PREVISAO_RETORNO && String(peca.DATA_PREVISAO_RETORNO).slice(0, 10),
        data_retorno_compras: peca.DATA_RETORNO_COMPRAS && String(peca.DATA_RETORNO_COMPRAS).slice(0, 10),
        observacao: peca.OBSERVACAO,
      };
      de[c] = mapa[c] == null ? '' : String(mapa[c]);
    });
    return de;
  });
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  const mexer = (c, v) => setValores((a) => ({ ...a, [c]: v }));

  const confirmar = async () => {
    setEnviando(true); setErro('');
    const e = await onConfirmar(valores);
    setEnviando(false);
    if (e) setErro(e);
  };

  const semCampos = !(etapa.campos || []).length;

  return (
    <div onClick={onFechar} style={{
      position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15,23,42,.55)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: compacto ? 10 : 32, overflowY: 'auto',
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 14, width: '100%', maxWidth: 520,
        marginTop: compacto ? 8 : 40, boxShadow: '0 20px 50px rgba(15,23,42,.25)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
          <strong style={{ flex: 1, fontSize: 15.5, color: '#0f172a' }}>{titulo}</strong>
          <button onClick={onFechar} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '16px 20px' }}>
          <p style={{ margin: '0 0 16px 0', fontSize: 13, color: '#64748b' }}>
            <strong style={{ color: '#334155' }}>{peca.LOJA}</strong> · {peca.DESCRICAO}
            {' · '}<span style={num}>{qtd(peca.QUANTIDADE)}</span> un.
          </p>

          {semCampos && (
            <p style={{ margin: '0 0 16px 0', fontSize: 13.5, color: '#475569' }}>
              Confirmar esta ação?
            </p>
          )}

          {(etapa.campos || []).map((c) => {
            const def = CAMPO[c] || { label: c, tipo: 'texto' };
            const opcional = opcionais.includes(c);
            return (
              <div key={c} style={{ marginBottom: 14 }}>
                <label style={rotuloStyle} htmlFor={`f-${c}`}>
                  {def.label}{opcional && <span style={{ fontWeight: 500, color: '#94a3b8' }}> (opcional)</span>}
                </label>
                {def.tipo === 'arquivo' ? (
                  <DocUpload
                    url={valores[c]} onUrl={(u) => mexer(c, u)}
                    doc={def.doc} accept={def.accept}
                    loja={peca.LOJA} chave={`P${peca.ID}`}
                  />
                ) : def.tipo === 'textarea' ? (
                  <textarea
                    id={`f-${c}`} rows={3} style={{ ...campoStyle, resize: 'vertical' }}
                    value={valores[c] || ''} onChange={(e) => mexer(c, e.target.value)}
                  />
                ) : (
                  <input
                    id={`f-${c}`}
                    type={def.tipo === 'data' ? 'date' : 'text'}
                    inputMode={def.tipo === 'decimal' ? 'decimal' : undefined}
                    style={campoStyle}
                    value={valores[c] || ''}
                    onChange={(e) => mexer(c, e.target.value)}
                  />
                )}
                {def.dica && <p style={{ margin: '5px 0 0 0', fontSize: 11.5, color: '#94a3b8' }}>{def.dica}</p>}
              </div>
            );
          })}

          {erro && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '10px 12px', background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
              <AlertTriangle size={15} /> {erro}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={onFechar} disabled={enviando}
              style={{ padding: '9px 16px', background: '#fff', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
              Cancelar
            </button>
            <button onClick={confirmar} disabled={enviando}
              style={{ padding: '9px 18px', background: enviando ? '#fcd34d' : COR, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 800, cursor: enviando ? 'wait' : 'pointer' }}>
              {enviando ? 'Salvando…' : 'Confirmar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
const ComprasReparos = () => {
  const compacto = useEhCompacto();
  const user = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const perfil = perfilNoModulo(user?.perfil, 'compras') || '';
  const ehAdmin = perfil === 'Admin';
  const podeCancelar = ['Admin', 'Compras'].includes(perfil);

  const [lista, setLista] = useState([]);
  const [etapas, setEtapas] = useState([]);
  const [complementos, setComplementos] = useState([]);
  const [caminhosEspeciais, setCaminhosEspeciais] = useState([]); // interno, estoque (do GET /fluxo)
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [fEquipe, setFEquipe] = useState('');
  const [fLoja, setFLoja] = useState('');
  const [fStatus, setFStatus] = useState('Pendente'); // abre no que precisa de ação; o placar troca num clique
  const [fOrigem, setFOrigem] = useState('');
  const [soMinhas, setSoMinhas] = useState(false);
  const [detalhe, setDetalhe] = useState(null);
  const [modal, setModal] = useState(null);
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(ITENS_POR_PAGINA); // 25 | 50 | 100 (seletor no rodapé)

  const abrirAnexo = async (url, nomePadrao) => {
    setErro('');
    try {
      await abrirArquivoComToken(`${API}/documento?url=${encodeURIComponent(url)}`, nomePadrao);
    } catch (e) {
      setErro(e.message);
    }
  };

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const [rp, rf] = await Promise.all([
        fetch(`${API}/itens`, { headers: auth() }),
        fetch(`${API}/fluxo`, { headers: auth() }),
      ]);
      if (!rp.ok) throw new Error(`HTTP ${rp.status}`);
      setLista(await rp.json());
      if (rf.ok) {
        const fluxo = await rf.json();
        setEtapas(fluxo.etapas || []);
        setComplementos(fluxo.complementos || []);
        setCaminhosEspeciais(fluxo.caminhosEspeciais || []);
      }
    } catch {
      setErro('Erro ao carregar a fila.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const etapaDe = useCallback((status) => etapas.find((e) => e.status === status) || null, [etapas]);
  const proximaDe = useCallback((status) => {
    const i = etapas.findIndex((e) => e.status === status);
    return i >= 0 && i + 1 < etapas.length ? etapas[i + 1] : null;
  }, [etapas]);

  // O complemento que cabe nesta peça agora (e é meu?).
  const complementoDaPeca = useCallback((peca) => {
    const c = complementos.find((x) => x.emStatus === peca.STATUS);
    if (!c) return null;
    const preenchido = (c.colunas || []).every((col) => peca[col]);
    const meu = ehAdmin || (c.quem || []).includes(perfil);
    return { ...c, preenchido, meu };
  }, [complementos, perfil, ehAdmin]);

  const meuPasso = useCallback((peca) => {
    if (peca.STATUS === CANCELADA) return false;
    const c = complementoDaPeca(peca);
    if (c && c.meu && !c.preenchido) return true;
    const prox = proximaDe(peca.STATUS);
    if (!prox) return false;
    return ehAdmin || (prox.quem || []).includes(perfil);
  }, [proximaDe, perfil, ehAdmin, complementoDaPeca]);

  const lojas = useMemo(() => [...new Set(lista.map((p) => p.LOJA).filter(Boolean))].sort(), [lista]);
  const equipes = useMemo(() => [...new Set(lista.map((x) => x.EQUIPE).filter(Boolean))].sort(), [lista]);

  const filtrada = useMemo(() => lista.filter((p) => {
    if (fEquipe && p.EQUIPE !== fEquipe) return false;
    if (fLoja && p.LOJA !== fLoja) return false;
    if (fStatus && p.STATUS !== fStatus) return false;
    if (fOrigem && p.ORIGEM !== fOrigem) return false;
    if (soMinhas && !meuPasso(p)) return false;
    return true;
  }), [lista, fEquipe, fLoja, fStatus, fOrigem, soMinhas, meuPasso]);

  // Ordenação por clique no cabeçalho (hook compartilhado). `ordem = null` = ordem
  // natural (a do servidor).
  const { ordem, alternarOrdem, ordenadas } = useOrdenacaoTabela(filtrada, valorDeOrdenacao);
  // Injeta o estado de ordenação + o estilo do cabeçalho nas colunas clicáveis.
  const ThOrd = (props) => <ThOrdBase {...props} ordem={ordem} alternarOrdem={alternarOrdem} estilo={th} />;

  // Volta para a 1ª página quando o recorte muda (senão fica numa página vazia).
  useEffect(() => { setPagina(1); }, [fEquipe, fLoja, fStatus, fOrigem, soMinhas]);
  const totalPaginas = Math.max(1, Math.ceil(filtrada.length / porPagina));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const linhas = useMemo(
    () => ordenadas.slice((paginaAtual - 1) * porPagina, paginaAtual * porPagina),
    [ordenadas, paginaAtual, porPagina],
  );

  const placar = useMemo(() => {
    const c = {};
    lista.forEach((p) => { c[p.STATUS] = (c[p.STATUS] || 0) + 1; });
    return c;
  }, [lista]);

  const minhas = useMemo(() => lista.filter(meuPasso).length, [lista, meuPasso]);

  const chamar = async (caminho, corpo) => {
    try {
      const r = await fetch(`${API}${caminho}`, {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify(corpo || {}),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) return d.message || 'Não foi possível concluir.';
      await carregar();
      return null;
    } catch (e) { return e.message; }
  };

  const abrirAvanco = (peca) => {
    const prox = proximaDe(peca.STATUS);
    if (!prox) return;
    setModal({
      peca, etapa: prox, titulo: prox.acao || `Avançar para ${prox.rotulo}`,
      onConfirmar: async (v) => {
        const e = await chamar(`/itens/${peca.ID}/avancar`, v);
        if (!e) setModal(null);
        return e;
      },
    });
  };

  // Um CAMINHO ESPECIAL a partir de Pendente (reparo interno / deixar no estoque).
  // ⚠️ Manda `caminho` no corpo do /avancar — é o que o backend lê em Pendente
  // para bifurcar e gravar a marca.
  const abrirCaminho = (peca, ce) => {
    setModal({
      peca,
      etapa: { campos: ce.campos || [] },
      titulo: ce.acao,
      opcionais: ce.rastreioOpcional ? ['codigo_rastreio'] : [],
      onConfirmar: async (v) => {
        const e = await chamar(`/itens/${peca.ID}/avancar`, { caminho: ce.caminho, ...v });
        if (!e) setModal(null);
        return e;
      },
    });
  };

  const abrirComplemento = (peca) => {
    const c = complementoDaPeca(peca);
    if (!c) return;
    setModal({
      peca, etapa: c, titulo: c.preenchido ? `Corrigir — ${c.rotulo}` : c.acao,
      onConfirmar: async (v) => {
        const e = await chamar(`/itens/${peca.ID}/complemento`, { complemento: c.id, ...v });
        if (!e) setModal(null);
        return e;
      },
    });
  };

  const abrirCorrecao = (peca) => {
    const atual = etapaDe(peca.STATUS);
    if (!atual || atual.inicial) return;
    setModal({
      peca, etapa: atual, titulo: `Corrigir — ${atual.rotulo}`,
      onConfirmar: async (v) => {
        const e = await chamar(`/itens/${peca.ID}/corrigir`, v);
        if (!e) setModal(null);
        return e;
      },
    });
  };

  const voltar = async (peca) => {
    const atual = etapaDe(peca.STATUS);
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Desfazer "${atual?.rotulo}" desta peça? O que foi informado neste passo será apagado.`)) return;
    const e = await chamar(`/itens/${peca.ID}/voltar`);
    if (e) setErro(e);
  };

  const cancelar = async (peca) => {
    // eslint-disable-next-line no-alert
    const motivo = window.prompt('Por que esta peça está saindo da fila?');
    if (motivo === null) return;
    const e = await chamar(`/itens/${peca.ID}/cancelar`, { motivo });
    if (e) setErro(e);
  };

  const reativar = async (peca) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Reativar esta peça? Ela volta para "Pendente" e o fluxo recomeça.')) return;
    const e = await chamar(`/itens/${peca.ID}/reativar`);
    if (e) setErro(e);
  };

  const exportar = () => {
    const linhasXlsx = filtrada.map((p) => ({
      'Peça': p.ID, 'Solicitação': p.ID_REPARO, 'Loja': p.LOJA, 'Equipe': p.EQUIPE,
      'Caminho': p.CAMINHO || 'normal',
      'Origem': p.ORIGEM === 'Central' ? 'Central de Pedidos' : (p.ORIGEM ? 'Fora da Central' : ''),
      'Produto': p.DESCRICAO, 'Código': p.CODIGO || '', 'Fornecedor': p.FORNECEDOR || '',
      'Fornecedor do reparo': p.FORNECEDOR_REPARO || '',
      'Quantidade': Number(p.QUANTIDADE), 'Status': p.STATUS,
      'Solicitação SAP': p.NUMERO_SOLICITACAO_SAP || '',
      'Data SAP': dataBR(p.DATA_SOLICITACAO_SAP),
      'Valor unitário': p.VALOR_UNITARIO == null ? '' : Number(p.VALOR_UNITARIO),
      'Total': p.VALOR_UNITARIO == null ? '' : Number(p.VALOR_UNITARIO) * Number(p.QUANTIDADE),
      'Nota fiscal': p.NUMERO_NOTA_FISCAL || '',
      'Envio': dataBR(p.DATA_ENVIO_MANUTENCAO),
      'Código de rastreio': p.CODIGO_RASTREIO || '',
      'Previsão de retorno': dataBR(p.DATA_PREVISAO_RETORNO),
      'Retorno': dataBR(p.DATA_RETORNO_COMPRAS),
      'Dias fora': diasFora(p) ?? '',
      'Observação': p.OBSERVACAO || '',
      'Problema': p.DESCRICAO_PROBLEMA, 'Solicitante': p.SOLICITANTE || '',
      'Aberta em': dataBR(p.CRIADO_EM),
      'Motivo do cancelamento': p.CANCELADA_MOTIVO || '',
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(linhasXlsx), 'Retirada');
    XLSX.writeFile(wb, `Retirada_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // ⚠️ Só o ÍCONE; o texto (`rotulo`) vira o `title` — aparece no hover. Mesmo
  //    padrão da tela de Inscritos (Processo Seletivo): quadradinho branco, ícone
  //    e borda coloridos, realce de fundo ao passar o mouse. `tom` escolhe a cor.
  const btn = (rotulo, Icone, onClick, tom = 'slate') => {
    const t = ACAO_TOM[tom] || ACAO_TOM.slate;
    return (
      <button type="button" onClick={onClick} key={rotulo} title={rotulo} aria-label={rotulo}
        style={{
          width: 34, height: 34, background: '#fff', color: t.cor,
          border: `1px solid ${t.borda}`, borderRadius: 8, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, transition: 'background-color .15s',
        }}
        onMouseOver={(e) => { e.currentTarget.style.backgroundColor = t.hover; }}
        onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}>
        <Icone size={17} />
      </button>
    );
  };

  const acoes = (p) => {
    if (p.STATUS === CANCELADA) {
      return podeCancelar ? [btn('Reativar', RotateCcw, () => reativar(p), 'verde')] : null;
    }
    const prox = proximaDe(p.STATUS);
    const atual = etapaDe(p.STATUS);
    const meu = meuPasso(p);
    const souDono = atual && !atual.inicial && (ehAdmin || (atual.quem || []).includes(perfil));
    const comp = complementoDaPeca(p);
    const lista_ = [];

    if (comp && comp.meu) {
      lista_.push(comp.preenchido
        ? btn(`Corrigir ${comp.rotulo.toLowerCase()}`, Pencil, () => abrirComplemento(p))
        : btn(comp.acao, Paperclip, () => abrirComplemento(p), 'primary'));
    }

    const travadoPeloComplemento = prox && prox.exigeComplemento && comp && !comp.preenchido;
    if (prox && meu && !travadoPeloComplemento) {
      lista_.push(btn(prox.acao || 'Avançar', ChevronRight, () => abrirAvanco(p), 'primary'));
    }

    // ⚠️ OS CAMINHOS ESPECIAIS só na PENDENTE, e só para quem move a esteira. São
    // ALTERNATIVAS ao "Lançar no SAP": reparo interno (pula o Fiscal) e deixar no
    // estoque (encerra direto). Vêm do GET /fluxo — a tela não os conhece.
    if (p.STATUS === 'Pendente' && (ehAdmin || perfil === 'Compras')) {
      const IconeCaminho = { interno: Hammer, estoque: Archive };
      caminhosEspeciais.forEach((ce) => {
        lista_.push(btn(ce.acao, IconeCaminho[ce.caminho] || Wrench, () => abrirCaminho(p, ce),
          ce.caminho === 'interno' ? 'interno' : 'estoque'));
      });
    }

    // ⚠️ "Corrigir" só quando a etapa TEM campo a corrigir — no Finalizado
    //    (campos vazios) o modal abriria em branco.
    if (souDono && (atual.campos || []).length) lista_.push(btn('Corrigir', Pencil, () => abrirCorrecao(p)));
    if (souDono) lista_.push(btn('Desfazer', Undo2, () => voltar(p)));
    if (podeCancelar && !atual?.final && !atual?.semCancelamento) lista_.push(btn('Cancelar', Ban, () => cancelar(p), 'vermelho'));

    if (lista_.length === 0 || travadoPeloComplemento) {
      const dono = travadoPeloComplemento ? (comp.quem || []) : (prox ? (prox.quem || []) : []);
      if (dono.length) {
        return (
          <>
            {lista_}
            <span style={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>
              aguarda {dono.join('/')}
            </span>
          </>
        );
      }
    }
    return lista_;
  };

  // -------------------------------------------------------------------
  // O REGISTRO DE COLUNAS. Cada uma sabe se desenhar a partir da peça. As FIXAS
  // aparecem sempre; as ESPECÍFICAS, conforme a etapa selecionada no placar.
  // ⚠️ É isto que "colunas por etapa" quer dizer: a mesma tabela mostra SAP+valor
  // na fiscal, a viagem no envio, o encerramento no finalizado — sem amontoar
  // tudo em toda linha.
  // -------------------------------------------------------------------
  const setDet = (p) => setDetalhe(detalhe === p.ID ? null : p.ID);
  const COL = {
    equipe: { titulo: 'Equipe', render: (p) => <SeloEquipe equipe={p.EQUIPE} /> },
    loja: { titulo: 'Loja', render: (p) => <span style={{ fontWeight: 800 }}>{p.LOJA}</span> },
    produto: {
      titulo: 'Produto',
      render: (p) => {
        const aberto = detalhe === p.ID;
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
              {p.TIPO_ITEM === 'Equipamento'
                ? <Wrench size={14} color="#7a5a08" title="É o próprio equipamento retirado" />
                : (p.ORIGEM === 'Central'
                  ? <Package size={14} color="#4f46e5" title="Peça da Central de Pedidos" />
                  : <PackageSearch size={14} color="#b45309" title="Peça de fora da Central" />)}
              <span style={{ fontWeight: 700, color: '#334155' }}>{p.DESCRICAO}</span>
              {p.TIPO_ITEM === 'Equipamento' && (
                <span style={{ fontSize: 11, fontWeight: 800, color: '#7a5a08' }}>equipamento</span>
              )}
              <SeloCaminho caminho={p.CAMINHO} />
            </div>
            <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 3 }}>
              {p.CODIGO ? `cód. ${p.CODIGO}` : 'sem código'}
              {p.FORNECEDOR ? ` · ${p.FORNECEDOR}` : ''} ·{' '}
              <button type="button" onClick={() => setDet(p)}
                style={{ border: 'none', background: 'none', padding: 0, color: COR, fontWeight: 700, fontSize: 11.5, cursor: 'pointer' }}>
                {aberto ? `ocultar o ${p.EQUIPE === 'TI' ? 'motivo' : 'problema'}` : `ver o ${p.EQUIPE === 'TI' ? 'motivo' : 'problema'}`}
              </button>
              {p.FOTO_URL && <ImgIcon size={12} color="#94a3b8" style={{ marginLeft: 5, verticalAlign: 'middle' }} />}
            </div>
          </div>
        );
      },
    },
    qtd: { titulo: 'Qtd', align: 'right', render: (p) => <span style={{ ...num, fontWeight: 700 }}>{qtd(p.QUANTIDADE)}</span> },
    etapa: {
      titulo: 'Etapa',
      render: (p) => {
        const est = estiloDe(p.STATUS);
        return (
          <span style={{ padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 800, background: est.bg, color: est.color, whiteSpace: 'nowrap' }}>
            {p.STATUS}
          </span>
        );
      },
    },
    abertaEm: { titulo: 'Aberta em', render: (p) => <span style={{ ...num, fontSize: 12.5, color: '#475569' }}>{dataBR(p.CRIADO_EM)}</span> },
    solicitante: { titulo: 'Solicitante', render: (p) => <span style={{ fontSize: 12.5, color: '#475569' }}>{p.SOLICITANTE || '—'}</span> },
    sap: {
      titulo: 'SAP',
      render: (p) => (p.NUMERO_SOLICITACAO_SAP
        ? <span style={{ ...num, fontSize: 12.5 }}>SAP <strong>{p.NUMERO_SOLICITACAO_SAP}</strong></span>
        : <span style={{ color: '#cbd5e1' }}>—</span>),
    },
    valor: {
      titulo: 'Valor', align: 'right',
      render: (p) => (p.VALOR_UNITARIO == null
        ? <span style={{ color: '#cbd5e1' }}>—</span>
        : (
          <span style={num}>
            <span style={{ fontWeight: 800 }}>{moeda(Number(p.VALOR_UNITARIO) * Number(p.QUANTIDADE))}</span>
            <span style={{ display: 'block', fontSize: 11, color: '#94a3b8' }}>un. {moeda(p.VALOR_UNITARIO)}</span>
          </span>
        )),
    },
    notaFiscal: {
      titulo: 'Nota fiscal',
      render: (p) => (
        <div style={{ fontSize: 12.5, ...num }}>
          {p.NUMERO_NOTA_FISCAL
            ? <div>NF <strong>{p.NUMERO_NOTA_FISCAL}</strong></div>
            : <span style={{ color: '#cbd5e1' }}>—</span>}
          {(p.NOTA_FISCAL_URL || p.TXT_URL) && (
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              {p.NOTA_FISCAL_URL && (
                <button onClick={() => abrirAnexo(p.NOTA_FISCAL_URL, 'nota-fiscal')}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11.5, color: COR, fontWeight: 700, border: 'none', background: 'none', padding: 0, cursor: 'pointer' }}>
                  <Paperclip size={11} /> NF
                </button>
              )}
              {p.TXT_URL && (
                <button onClick={() => abrirAnexo(p.TXT_URL, 'arquivo.txt')}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11.5, color: COR, fontWeight: 700, border: 'none', background: 'none', padding: 0, cursor: 'pointer' }}>
                  <Paperclip size={11} /> TXT
                </button>
              )}
            </div>
          )}
        </div>
      ),
    },
    fornecedorReparo: { titulo: 'Fornecedor do reparo', render: (p) => <span style={{ fontSize: 12.5, color: '#475569' }}>{p.FORNECEDOR_REPARO || '—'}</span> },
    saiu: {
      titulo: 'Saiu',
      render: (p) => (p.DATA_ENVIO_MANUTENCAO
        ? <span style={{ ...num, fontSize: 12.5 }}>{dataBR(p.DATA_ENVIO_MANUTENCAO)}</span>
        : <span style={{ color: '#cbd5e1' }}>—</span>),
    },
    rastreio: {
      titulo: 'Rastreio',
      render: (p) => (p.CODIGO_RASTREIO
        ? <span style={{ ...num, fontSize: 12, color: '#0f766e' }}>{p.CODIGO_RASTREIO}</span>
        : <span style={{ color: '#cbd5e1' }}>—</span>),
    },
    previsao: {
      titulo: 'Previsão',
      render: (p) => {
        if (!p.DATA_PREVISAO_RETORNO) return <span style={{ color: '#cbd5e1' }}>—</span>;
        const atras = atrasadaNaPrevisao(p);
        return (
          <span style={{ ...num, fontSize: 12.5, color: atras ? '#b91c1c' : '#64748b', fontWeight: atras ? 700 : 400 }}>
            {dataBR(p.DATA_PREVISAO_RETORNO)}{atras ? ' · ATRASADA' : ''}
          </span>
        );
      },
    },
    saiuVoltou: {
      titulo: 'Saiu / Voltou',
      render: (p) => (
        <span style={{ ...num, fontSize: 12.5, color: '#475569' }}>
          {p.DATA_ENVIO_MANUTENCAO && <div>saiu {dataBR(p.DATA_ENVIO_MANUTENCAO)}</div>}
          {p.DATA_RETORNO_COMPRAS
            ? <div style={{ color: '#047857', fontWeight: 700 }}>voltou {dataBR(p.DATA_RETORNO_COMPRAS)}</div>
            : <div style={{ color: '#cbd5e1' }}>ainda fora</div>}
        </span>
      ),
    },
    diasFora: {
      titulo: 'Dias fora', align: 'right',
      render: (p) => {
        const d = diasFora(p);
        return d == null ? <span style={{ color: '#cbd5e1' }}>—</span> : <span style={{ ...num, fontWeight: 700 }}>{d}d</span>;
      },
    },
    comoEncerrou: {
      titulo: 'Encerramento',
      render: (p) => <span style={{ fontSize: 12.5, color: '#475569' }}>{comoEncerrou(p)}</span>,
    },
    observacao: {
      titulo: 'Observação',
      render: (p) => (p.OBSERVACAO
        ? <span style={{ fontSize: 12.5, color: '#475569', whiteSpace: 'normal' }}>{p.OBSERVACAO}</span>
        : <span style={{ color: '#cbd5e1' }}>—</span>),
    },
    motivo: {
      titulo: 'Motivo',
      render: (p) => (p.CANCELADA_MOTIVO
        ? <span style={{ fontSize: 12.5, color: '#64748b', whiteSpace: 'normal' }}>{p.CANCELADA_MOTIVO}</span>
        : <span style={{ color: '#cbd5e1' }}>—</span>),
    },
  };

  // As colunas específicas de cada etapa (o placar é o seletor). Sem etapa
  // escolhida, mostra "Etapa" (o status na linha), já que a seleção não implica uma.
  const COLS_POR_STATUS = {
    '': ['etapa'],
    'Pendente': ['abertaEm', 'solicitante'],
    'Enviado para Fiscal': ['sap', 'valor', 'notaFiscal'],
    'Enviado para Reparo': ['fornecedorReparo', 'notaFiscal', 'saiu', 'rastreio', 'previsao', 'valor'],
    'Retorno do Reparo': ['saiuVoltou', 'diasFora', 'valor'],
    'Finalizado': ['comoEncerrou', 'observacao'],
    'Cancelada': ['motivo'],
  };
  const chavesColunas = ['equipe', 'loja', 'produto', 'qtd', ...(COLS_POR_STATUS[fStatus] || ['etapa'])];
  const totalCols = chavesColunas.length + 1; // + a coluna de Ações

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, flex: 1, minWidth: 200, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
          Retirada de Equipamento/Peças
        </h3>
        {filtrada.length > 0 && (
          <button onClick={exportar} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#fff', color: COR, border: '1px solid #fde68a', borderRadius: 8, fontWeight: 800, cursor: 'pointer' }}>
            <FileDown size={16} /> Exportar Excel
          </button>
        )}
      </div>
      <p style={{ margin: '0 0 14px 0', fontSize: 12.5, color: '#94a3b8' }}>
        Cada etapa só avança com o dado dela. Na Pendente, o Compras escolhe o caminho: lançar no SAP,
        mandar para reparo interno (funcionário do Grupo, sem nota) ou deixar no estoque.
      </p>

      {minhas > 0 && (
        <button type="button" onClick={() => setSoMinhas((v) => !v)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 14,
            padding: '9px 15px', borderRadius: 10, cursor: 'pointer', fontWeight: 800, fontSize: 13.5,
            border: `${soMinhas ? 2 : 1}px solid ${soMinhas ? '#92400e' : '#fde68a'}`,
            background: '#fffbeb', color: '#92400e',
          }}>
          <Wrench size={15} />
          {minhas} {minhas === 1 ? 'item espera' : 'itens esperam'} por você
          {soMinhas && <span style={{ fontWeight: 600 }}>· mostrando só elas</span>}
        </button>
      )}

      {/* Placar por etapa — a ordem vem da API, então ele lê como o fluxo. É o
          SELETOR de etapa, e é ele que troca as colunas da tabela. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14, alignItems: 'center' }}>
        {etapas.map((e, i) => {
          const n = placar[e.status] || 0;
          const est = estiloDe(e.status);
          const ativo = fStatus === e.status;
          return (
            <React.Fragment key={e.status}>
              {i > 0 && <ChevronRight size={13} color="#cbd5e1" />}
              <button type="button" onClick={() => setFStatus(ativo ? '' : e.status)}
                title={e.descricao}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                  background: est.bg, color: est.color, borderRadius: 999, padding: '5px 12px',
                  fontSize: 12, fontWeight: 800, opacity: n ? 1 : 0.5,
                  border: `${ativo ? 2 : 1}px solid ${ativo ? est.color : 'transparent'}`,
                }}>
                {e.rotulo} <span style={num}>{n}</span>
              </button>
            </React.Fragment>
          );
        })}
        {placar[CANCELADA] > 0 && (
          <button type="button" onClick={() => setFStatus(fStatus === CANCELADA ? '' : CANCELADA)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginLeft: 6,
              background: ESTILO.Cancelada.bg, color: ESTILO.Cancelada.color, borderRadius: 999,
              padding: '5px 12px', fontSize: 12, fontWeight: 800,
              border: `${fStatus === CANCELADA ? 2 : 1}px solid ${fStatus === CANCELADA ? '#64748b' : 'transparent'}`,
            }}>
            Cancelada <span style={num}>{placar[CANCELADA]}</span>
          </button>
        )}
      </div>

      {/* Cartão de filtros (padrão das "Avaliações gravadas"). */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 12, marginBottom: 14 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#475569', fontWeight: 700, fontSize: 13 }}><Filter size={15} /> Filtros</span>
        {equipes.length > 1 && (
          <select value={fEquipe} onChange={(e) => setFEquipe(e.target.value)} style={sel}>
            <option value="">T.I e Manutenção</option>
            {equipes.map((e) => (
              <option key={e} value={e}>{`Só ${(EQUIPES_SELO[e] || {}).rotulo || e}`}</option>
            ))}
          </select>
        )}
        <select value={fLoja} onChange={(e) => setFLoja(e.target.value)} style={sel}>
          <option value="">Todas as lojas</option>
          {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        {/* ⚠️ O filtro de origem só existe onde há origem (a retirada de equipamento
            não vem de catálogo — ORIGEM nula). */}
        {lista.some((p) => p.ORIGEM) && (
          <select value={fOrigem} onChange={(e) => setFOrigem(e.target.value)} style={sel}>
            <option value="">Central e fora dela</option>
            <option value="Central">Só da Central de Pedidos</option>
            <option value="Fora">Só de fora da Central</option>
          </select>
        )}
        {(fEquipe || fLoja || fStatus || fOrigem || soMinhas) && (
          <button onClick={() => { setFEquipe(''); setFLoja(''); setFStatus(''); setFOrigem(''); setSoMinhas(false); }}
            style={{ padding: '8px 12px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            Limpar
          </button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 13, color: '#94a3b8', ...num }}>
          {filtrada.length} de {lista.length}
        </span>
      </div>

      {erro && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 12, fontSize: 13.5, fontWeight: 600 }}>
          <AlertTriangle size={16} /> {erro}
          <button onClick={() => setErro('')} style={{ marginLeft: 'auto', border: 'none', background: 'none', cursor: 'pointer', color: '#991b1b' }}><X size={15} /></button>
        </div>
      )}

      {carregando ? (
        <p style={{ color: '#64748b' }}>Carregando…</p>
      ) : lista.length === 0 ? (
        <div style={{ padding: 36, textAlign: 'center', background: '#f8fafc', borderRadius: 12, color: '#64748b' }}>
          <Wrench size={26} color={COR} />
          <p style={{ fontWeight: 700, color: '#0f172a', margin: '12px 0 4px 0' }}>Nenhuma retirada pedida</p>
          <p style={{ fontSize: 13.5, margin: 0 }}>As lojas abrem a solicitação no módulo Logística.</p>
        </div>
      ) : filtrada.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', background: '#f8fafc', borderRadius: 12, color: '#64748b' }}>
          Nenhum item para os filtros escolhidos.
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: compacto ? 720 : 980 }}>
              <thead style={{ background: '#f8fafc' }}>
                <tr>
                  {chavesColunas.map((k) => {
                    const campo = CAMPO_COL[k];
                    const align = COL[k].align === 'right' ? 'right' : 'left';
                    return campo
                      ? <ThOrd key={k} campo={campo} align={align}>{COL[k].titulo}</ThOrd>
                      : <th key={k} style={{ ...th, textAlign: align }}>{COL[k].titulo}</th>;
                  })}
                  <th style={{ ...th, textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((p) => {
                  const aberto = detalhe === p.ID;
                  const meu = meuPasso(p);
                  return (
                    <React.Fragment key={p.ID}>
                      <tr style={{
                        borderBottom: aberto ? 'none' : '1px solid #f1f5f9',
                        borderLeft: meu ? `3px solid ${COR}` : '3px solid transparent',
                        opacity: p.STATUS === CANCELADA ? 0.6 : 1,
                      }}>
                        {chavesColunas.map((k) => (
                          <td key={k} style={{ ...td, textAlign: COL[k].align === 'right' ? 'right' : 'left' }}>
                            {COL[k].render(p)}
                          </td>
                        ))}
                        <td style={{ ...td, textAlign: 'right' }}>
                          <div style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            {acoes(p)}
                          </div>
                        </td>
                      </tr>

                      {aberto && (
                        <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td colSpan={totalCols} style={{ padding: '0 14px 14px 14px', background: '#f8fafc' }}>
                            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start', paddingTop: 12 }}>
                              <div style={{ flex: '1 1 320px', minWidth: 0 }}>
                                <p style={{ margin: '0 0 4px 0', fontSize: 11.5, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>
                                  {p.EQUIPE === 'TI' ? 'Motivo' : 'Problema'} — solicitação #{p.ID_REPARO}
                                </p>
                                <p style={{ margin: 0, fontSize: 13.5, color: '#334155', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                                  {p.DESCRICAO_PROBLEMA}
                                </p>
                                <p style={{ margin: '8px 0 0 0', fontSize: 12, color: '#94a3b8' }}>
                                  Aberta em {dataBR(p.CRIADO_EM)}{p.SOLICITANTE ? ` por ${p.SOLICITANTE}` : ''}
                                  {p.ATUALIZADO_POR ? ` · último passo por ${p.ATUALIZADO_POR}` : ''}
                                </p>
                                {p.FORNECEDOR_REPARO && (
                                  <p style={{ margin: '6px 0 0 0', fontSize: 12.5, color: '#475569' }}>
                                    <strong>Fornecedor do reparo:</strong> {p.FORNECEDOR_REPARO}
                                  </p>
                                )}
                                {p.OBSERVACAO && (
                                  <p style={{ margin: '6px 0 0 0', fontSize: 12.5, color: '#475569' }}>Obs.: {p.OBSERVACAO}</p>
                                )}
                              </div>
                              {p.FOTO_URL && (
                                <ImagemSharePoint apiBase={API} url={p.FOTO_URL} alt="Foto do problema" size={150} radius={8} />
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Rodapé de paginação (padrão das "Avaliações gravadas"). */}
          <div style={{ padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>
              Página {paginaAtual} de {totalPaginas}
              <span style={{ marginLeft: 10, fontWeight: 600 }}>(Total: {filtrada.length} {filtrada.length === 1 ? 'item' : 'itens'})</span>
            </span>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#64748b' }}>
                Linhas por página:
                <select value={porPagina} onChange={(e) => { setPorPagina(Number(e.target.value)); setPagina(1); }}
                  style={{ padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, color: '#475569', background: '#fff', cursor: 'pointer' }}>
                  {[25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setPagina((p) => Math.max(p - 1, 1))} disabled={paginaAtual === 1} style={btnPag(paginaAtual === 1)}>Anterior</button>
                <button onClick={() => setPagina((p) => Math.min(p + 1, totalPaginas))} disabled={paginaAtual === totalPaginas} style={btnPag(paginaAtual === totalPaginas)}>Próxima</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {modal && (
        <PassoModal
          peca={modal.peca} etapa={modal.etapa} titulo={modal.titulo} opcionais={modal.opcionais}
          onFechar={() => setModal(null)} onConfirmar={modal.onConfirmar}
        />
      )}
    </div>
  );
};

export default ComprasReparos;
